/**
 * "My Logs" — everything this rep has done, across every account.
 *
 * The per-account timeline answers "what happened at this bar?". This
 * answers the other question a rep has — "what have I actually done?"
 * — which otherwise means opening accounts one at a time and
 * reconstructing it from memory.
 *
 * Three sources in one feed: visits logged, accounts flagged for admin
 * review, and accounts skipped on Today's Plan. A rep thinking about
 * their week doesn't sort those into categories, so neither does the
 * list — filter chips are there for when they want to.
 *
 * Grouped by the day the thing HAPPENED (a backdated visit lands on the
 * day of the visit, not the day it was typed), with the time it was
 * recorded on each row.
 */

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SkipNextOutlinedIcon from '@mui/icons-material/SkipNextOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Pagination,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useSectionExport } from '../../hooks/useSectionExport';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num,
  type ExcelWorkbookSpec,
} from '../../util/excel-export';

import {
  fetchMyLogs,
  VISIT_CHANNEL_LABELS,
  VISIT_OUTCOME_LABELS,
  VISIT_OUTCOME_TONES,
  type LogEntryKind,
  type MyLogEntry,
  type VisitNoteDraft,
} from '../../api/field';
import { EditedMarker } from './EditedMarker';
import { VisitNoteDialog } from './VisitNoteDialog';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const IVORY = '#fdf9f0';
const HAIRLINE = 'rgba(187, 140, 63, 0.22)';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

/** Enough that a normal week fits on one page. */
const PAGE_SIZE = 25;

/** The API's `limit` ceiling, and a guard against a runaway loop. */
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 200;

/** Tone + icon per entry kind. Flags borrow the "declined" red because
 *  a flag is an escalation; skips stay neutral grey because skipping is
 *  a non-event that merely needs recording. */
const KIND_META: Record<LogEntryKind, { tone: string; icon: typeof EventNoteIcon; label: string }> =
  {
    visit: { tone: GOLD, icon: EventNoteIcon, label: 'Logged' },
    flag: { tone: '#b22f2f', icon: FlagOutlinedIcon, label: 'Flagged for review' },
    skip: { tone: 'rgba(15, 10, 7, 0.35)', icon: SkipNextOutlinedIcon, label: 'Skipped' },
  };

/** "Today" / "Yesterday" / "Tue, 12 Aug".
 *
 *  Parsed from local parts — `new Date('2026-08-20')` is midnight UTC
 *  and shifts a day backwards for anyone west of Greenwich.
 */
function dayHeading(iso: string): string {
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);

  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** The local calendar day of a timestamp, as a Date Excel shows as
 *  that day — slicing the ISO string would give the UTC day. */
function localDay(iso: string): Date | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "20 Aug" — year appended only when it isn't the current one, since
 *  a rep scanning this week doesn't need "2026" on every row. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(y === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

export function MyLogsTab({ onAddAccount }: { onAddAccount: () => void }) {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<LogEntryKind | null>(null);
  const [editing, setEditing] = useState<{
    accountId: number;
    accountName: string;
    note: VisitNoteDraft;
  } | null>(null);

  const query = useQuery({
    queryKey: ['field', 'my-logs', page, kind],
    queryFn: () => fetchMyLogs(PAGE_SIZE, (page - 1) * PAGE_SIZE, kind ?? undefined),
    staleTime: 30_000,
    // Paging keeps the previous page on screen while the next loads,
    // rather than collapsing to skeletons and jumping the scroll.
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Changing the filter invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [kind]);

  const counts = {
    visit: query.data?.visit_count ?? 0,
    flag: query.data?.flag_count ?? 0,
    skip: query.data?.skip_count ?? 0,
  };
  const grandTotal = counts.visit + counts.flag + counts.skip;

  // Every entry under the current filter, not just this page — fetched
  // fresh at click time at the API's largest page size.
  useSectionExport(
    async (): Promise<ExcelWorkbookSpec | null> => {
      const all: MyLogEntry[] = [];
      for (let i = 0; i < EXPORT_MAX_PAGES; i++) {
        const res = await fetchMyLogs(EXPORT_PAGE_SIZE, i * EXPORT_PAGE_SIZE, kind ?? undefined);
        all.push(...res.items);
        if (res.items.length < EXPORT_PAGE_SIZE || all.length >= res.total) break;
      }
      const rows = all.map((e) => ({
        ...e,
        name: e.account_name,
        address: e.account_address,
        city: e.account_city,
        state_code: e.account_state,
      }));
      type Row = (typeof rows)[number];
      return {
        title: 'My Logs',
        filters: [{ label: 'Entry type', value: kind ? KIND_META[kind].label : 'All entries' }],
        sheets: [
          excelSheet({
            name: 'My Logs',
            rows,
            columns: [
              { header: 'Date', value: (r) => isoDate(r.occurred_on), format: 'date' },
              { header: 'Type', value: (r) => KIND_META[r.kind].label, width: 18 },
              // County and ZIP aren't on log rows.
              ...accountColumns<Row>().filter((c) => c.header !== 'County' && c.header !== 'ZIP'),
              {
                header: 'Manually Added',
                value: (r) => (r.account_is_manual ? 'Yes' : ''),
                width: 10,
              },
              {
                header: 'Outcome',
                value: (r) =>
                  r.kind === 'visit' && r.outcome
                    ? VISIT_OUTCOME_LABELS[r.outcome]
                    : KIND_META[r.kind].label,
                width: 18,
              },
              {
                header: 'Channel',
                value: (r) => (r.channel ? VISIT_CHANNEL_LABELS[r.channel] : ''),
                width: 16,
              },
              {
                header: 'Products',
                value: (r) =>
                  r.products
                    .map((p) => {
                      const qty = num(p.quantity);
                      return qty !== null ? `${qty} ${p.unit} · ${p.product_name}` : p.product_name;
                    })
                    .join('; '),
                width: 36,
              },
              {
                header: 'Note / Reason',
                value: (r) =>
                  r.kind === 'visit'
                    ? r.note_text
                    : r.kind === 'flag'
                      ? r.reason || 'Flagged for admin review.'
                      : 'Skipped on Today’s Plan',
                width: 60,
              },
              { header: 'Logged On', value: (r) => localDay(r.logged_at), format: 'date' },
              { header: 'Logged At', value: (r) => clockTime(r.logged_at), width: 10 },
            ],
          }),
        ],
      };
    },
    query.isSuccess && grandTotal > 0,
  );

  if (query.isPending) {
    return (
      <Stack gap={1.25}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={78} />
        ))}
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
        Couldn’t load your logs.
      </Alert>
    );
  }

  if (grandTotal === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          px: 2,
          border: '1px dashed rgba(187, 140, 63, 0.35)',
          borderRadius: 2,
          bgcolor: IVORY,
        }}
      >
        <EventNoteIcon sx={{ fontSize: 34, color: 'rgba(187, 140, 63, 0.6)', mb: 1 }} />
        <Typography
          sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 700, color: INK, mb: 0.5 }}
        >
          Nothing logged yet
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13,
            color: 'rgba(15, 10, 7, 0.6)',
            maxWidth: 480,
            mx: 'auto',
            mb: 2,
          }}
        >
          Every visit you log, account you flag for review, and account you skip collects here — so
          you can see your week at a glance without opening accounts one by one.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onAddAccount}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            color: GOLD,
            borderColor: 'rgba(187, 140, 63, 0.5)',
          }}
        >
          Add an Account
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ mb: 2, maxWidth: 940 }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13,
            color: 'rgba(15, 10, 7, 0.62)',
            lineHeight: 1.55,
          }}
        >
          Everything you’ve logged, flagged and skipped — newest first.
        </Typography>

        {/* Counts come from the unfiltered totals, so they stay put as
            you filter and page rather than describing what's on screen. */}
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          <FilterChip
            label={`All ${grandTotal}`}
            active={kind === null}
            tone={INK}
            onClick={() => setKind(null)}
          />
          {(['visit', 'flag', 'skip'] as const).map((k) => (
            <FilterChip
              key={k}
              label={`${KIND_META[k].label} ${counts[k]}`}
              active={kind === k}
              tone={KIND_META[k].tone}
              disabled={counts[k] === 0}
              onClick={() => setKind(k)}
            />
          ))}
        </Stack>
      </Stack>

      {items.length === 0 ? (
        <Alert severity="info" sx={{ fontFamily: FONT_INTER, fontSize: 13 }}>
          Nothing of that kind yet.
        </Alert>
      ) : (
        <Stack
          gap={0}
          sx={{
            opacity: query.isFetching ? 0.6 : 1,
            transition: 'opacity 150ms',
            // A feed doesn't benefit from the full page width: at
            // ~1500px the outcome chip ends up a thousand pixels from
            // the account name it describes, with dead space between.
            maxWidth: 940,
          }}
        >
          {items.map((entry, i) => {
            const previous = items[i - 1];
            const heading =
              !previous || previous.occurred_on !== entry.occurred_on
                ? dayHeading(entry.occurred_on)
                : null;
            return (
              <LogEntryRow
                key={entry.id}
                entry={entry}
                dayHeading={heading}
                // The rail stops at the last entry rather than trailing
                // into empty space below the feed.
                isLast={i === items.length - 1}
                onEdit={(note) =>
                  setEditing({
                    accountId: entry.account_id,
                    accountName: entry.account_name,
                    note,
                  })
                }
              />
            );
          })}
        </Stack>
      )}

      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            sx={{
              '& .MuiPaginationItem-root': {
                fontFamily: FONT_INTER,
                fontSize: 13,
                '&.Mui-selected': {
                  bgcolor: 'rgba(187, 140, 63, 0.16)',
                  color: '#8a6526',
                  fontWeight: 700,
                  '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.24)' },
                },
              },
            }}
          />
        </Stack>
      )}

      {editing && (
        <VisitNoteDialog
          open
          accountId={editing.accountId}
          accountName={editing.accountName}
          note={editing.note}
          onClose={() => setEditing(null)}
        />
      )}
    </Box>
  );
}

function FilterChip({
  label,
  active,
  tone,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  tone: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={disabled ? undefined : onClick}
      sx={{
        height: 26,
        px: 0.25,
        fontFamily: FONT_INTER,
        fontSize: 11.5,
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color: active ? '#fff' : tone,
        bgcolor: active ? tone : 'transparent',
        border: `1px solid ${active ? tone : 'rgba(15, 10, 7, 0.14)'}`,
        '&:hover': { bgcolor: active ? tone : 'rgba(15, 10, 7, 0.04)' },
      }}
    />
  );
}

function LogEntryRow({
  entry,
  dayHeading: heading,
  isLast,
  onEdit,
}: {
  entry: MyLogEntry;
  dayHeading: string | null;
  isLast: boolean;
  onEdit: (note: VisitNoteDraft) => void;
}) {
  const navigate = useNavigate();
  const meta = KIND_META[entry.kind];
  const Icon = meta.icon;

  // A visit's tone carries its outcome, which is the fastest read on
  // the row. Flags and skips have no outcome, so they use their kind's
  // tone instead.
  const tone =
    entry.kind === 'visit' && entry.outcome
      ? (VISIT_OUTCOME_TONES[entry.outcome] ?? GOLD)
      : meta.tone;

  const location = [entry.account_address, entry.account_city].filter(Boolean).join(', ') || null;
  // The visit happened on one day and was typed on another.
  const wasBackdated = entry.logged_at.slice(0, 10) !== entry.occurred_on;

  const open = () => navigate(`/regional-sales/accounts/${entry.account_id}`);

  // Every visit in this log is the rep's own, so every one is editable.
  const draft: VisitNoteDraft | null =
    entry.kind === 'visit' && entry.note_id !== null && entry.channel && entry.outcome
      ? {
          id: entry.note_id,
          visit_date: entry.occurred_on,
          channel: entry.channel,
          outcome: entry.outcome,
          note_text: entry.note_text ?? '',
          products: entry.products,
        }
      : null;

  return (
    <>
      {heading && (
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ mt: 3, mb: 1.5 }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.45)',
              flexShrink: 0,
            }}
          >
            {heading}
          </Typography>
          {/* Hairline out to the edge — a bare caption floating above
              the rows reads as a stray label, not a divider. */}
          <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'rgba(187, 140, 63, 0.2)' }} />
        </Stack>
      )}

      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Time gutter. Pulling the timestamp to the left, aligned in
            its own column, turns the feed into an agenda a rep can scan
            vertically — and puts it beside the entry instead of a
            thousand pixels away on the right. */}
        <Box
          sx={{
            width: { xs: 62, sm: 76 },
            flexShrink: 0,
            pt: 1.75,
            pr: 1.5,
            textAlign: 'right',
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11.5,
              fontWeight: 600,
              color: 'rgba(15, 10, 7, 0.55)',
              whiteSpace: 'nowrap',
            }}
          >
            {clockTime(entry.logged_at)}
          </Typography>
          {wasBackdated && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 9.5,
                color: 'rgba(15, 10, 7, 0.34)',
                whiteSpace: 'nowrap',
              }}
            >
              logged {shortDate(entry.logged_at.slice(0, 10))}
            </Typography>
          )}
        </Box>

        {/* Rail — a dot per entry, connected down the day. Carries the
            entry's tone, so the colour cue lives on the timeline rather
            than as a slab on the card edge. */}
        <Box
          sx={{
            width: 22,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              mt: 1.9,
              width: 11,
              height: 11,
              borderRadius: '50%',
              bgcolor: '#fff',
              border: `2.5px solid ${tone}`,
              flexShrink: 0,
              zIndex: 1,
            }}
          />
          {!isLast && (
            <Box sx={{ flexGrow: 1, width: '2px', bgcolor: 'rgba(15, 10, 7, 0.07)', mt: 0.5 }} />
          )}
        </Box>

        <Box
          onClick={open}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              open();
            }
          }}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            ml: 1.25,
            mb: 1.25,
            px: 2,
            py: 1.6,
            bgcolor: '#fff',
            border: '1px solid rgba(15, 10, 7, 0.08)',
            borderRadius: 2,
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(15, 10, 7, 0.03)',
            transition: 'box-shadow 160ms ease, border-color 160ms ease',
            '&:hover': {
              borderColor: 'rgba(187, 140, 63, 0.42)',
              boxShadow: '0 2px 6px rgba(15, 10, 7, 0.05), 0 10px 24px rgba(15, 10, 7, 0.06)',
            },
            '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
          }}
        >
          <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 0.25 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 14,
                fontWeight: 700,
                color: INK,
                minWidth: 0,
              }}
            >
              {entry.account_name}
            </Typography>
            {entry.account_state && (
              <Typography sx={{ fontFamily: FONT_INTER, fontSize: 11, color: 'rgba(15,10,7,0.4)' }}>
                {entry.account_state}
              </Typography>
            )}
            {entry.account_is_manual && (
              <Chip
                size="small"
                label="Manually added"
                sx={{
                  height: 17,
                  fontFamily: FONT_INTER,
                  fontSize: 9.5,
                  fontWeight: 700,
                  bgcolor: 'rgba(187, 140, 63, 0.14)',
                  color: '#8a6526',
                }}
              />
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Chip
              size="small"
              icon={<Icon sx={{ fontSize: 12, color: `${tone} !important` }} />}
              label={
                entry.kind === 'visit' && entry.outcome
                  ? VISIT_OUTCOME_LABELS[entry.outcome]
                  : meta.label
              }
              sx={{
                height: 21,
                flexShrink: 0,
                fontFamily: FONT_INTER,
                fontSize: 10.5,
                fontWeight: 700,
                bgcolor: `${tone}14`,
                color: tone,
                border: `1px solid ${tone}44`,
                '& .MuiChip-icon': { ml: 0.6, mr: -0.2 },
              }}
            />
            {draft && (
              <Tooltip title="Edit this visit">
                <IconButton
                  size="small"
                  aria-label="Edit this visit"
                  // The card itself opens the account; the pencil
                  // shouldn't do both.
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(draft);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{
                    alignSelf: 'center',
                    p: 0.4,
                    color: 'rgba(15, 10, 7, 0.4)',
                    '&:hover': { color: GOLD },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Address and channel on one muted line — both are context,
              and separating them into two rows gave secondary
              information more vertical weight than the note itself. */}
          {(location || entry.channel) && (
            <Typography
              sx={{
                mb: 0.9,
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.45)',
              }}
            >
              {[location, entry.channel ? VISIT_CHANNEL_LABELS[entry.channel] : null]
                .filter(Boolean)
                .join('  ·  ')}
              <EditedMarker
                editedAt={entry.edited_at}
                editedByName={entry.edited_by_name}
                someoneElse={entry.edited_by_someone_else}
              />
            </Typography>
          )}

          {entry.kind === 'visit' && entry.note_text && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 13,
                lineHeight: 1.6,
                color: 'rgba(15, 10, 7, 0.8)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {entry.note_text}
            </Typography>
          )}

          {entry.kind === 'flag' && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 13,
                lineHeight: 1.6,
                color: 'rgba(15, 10, 7, 0.8)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {entry.reason || 'Flagged for admin review.'}
            </Typography>
          )}

          {entry.kind === 'skip' && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12.5,
                fontStyle: 'italic',
                color: 'rgba(15, 10, 7, 0.5)',
              }}
            >
              Skipped on Today’s Plan — back on the list next time.
            </Typography>
          )}

          {entry.products.length > 0 && (
            <Stack direction="row" gap={0.6} flexWrap="wrap" rowGap={0.6} sx={{ mt: 1.1 }}>
              {entry.products.map((p) => (
                <Chip
                  key={p.product_id}
                  size="small"
                  label={
                    p.quantity ? `${p.quantity} ${p.unit} · ${p.product_name}` : p.product_name
                  }
                  sx={{
                    height: 20,
                    fontFamily: FONT_INTER,
                    fontSize: 10.5,
                    fontWeight: 600,
                    bgcolor: 'rgba(187, 140, 63, 0.1)',
                    color: '#8a6526',
                    border: `1px solid ${HAIRLINE}`,
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </>
  );
}
